import { type AssetLabelInput, isAssetLabelInput } from 'components/market/asset/AssetLabelService';
import { useAssetRiskMetricsSuspenseQuery } from 'generated/graphql';
import type { FunctionComponent } from 'react';
import {
  assetMetricColumn,
  type AssetMetricValues,
  clusterColumn,
  nameColumn,
  priceChangesGroupColumns,
  symbolColumn,
} from '../../../technical/grids/SharedReportColumns';

import type { ColDef, FilterModel, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { useReportAssetGroup } from 'components/UseReportAssetGroups';
import { formatNumber } from 'components/formatter.utils';
import { usePriceChanges } from 'components/portfolio/openPositions/UsePriceChanges';
import GAgGridPresets from 'components/technical/grids/GAgGridPresets';
import { metricColumnsConfiguration } from './AssetScreenerService';
import { assetScreenerDefaultPresets } from './assetScreenerDefaultPresets';
import type { NotVerifiedAssetWithId } from '../AssetService';
import isNil from 'lodash/fp/isNil';

export type AssetRowType = { asset: AssetLabelInput };

type AssetScreenerTableProps = {
  scores:
    | {
        assetScores: {
          asset: { id: string };
          score: Decimal;
        }[];
        multifactor: {
          multifactor: {
            id: number;
            name: string;
          };
        };
      }[]
    | undefined;
  presetKey: string;
  assets: NotVerifiedAssetWithId[];
  onFiltersChanged?: (model: FilterModel) => void;
  defaultFilters?: FilterModel | null; // null means that there are no filters and we shouldn't set them
};

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

const sidebar = {
  toolPanels: ['columns', 'filters'],
};

const AssetScreenerTable: FunctionComponent<AssetScreenerTableProps> = ({
  scores,
  presetKey,
  assets,
  onFiltersChanged,
  defaultFilters,
}) => {
  const reportAssetGroup = useReportAssetGroup();
  const assetIds = assets.map((asset) => asset.id);
  const pricesChangesResult = usePriceChanges(assetIds);

  const assetRiskMetricsQueryResult = useAssetRiskMetricsSuspenseQuery({
    variables: {
      assetIds,
    },
  });

  const metricsByAsset = new Map<string, AssetMetricValues>(
    assetRiskMetricsQueryResult.data?.assets.details.map((assetMetrics) => [
      assetMetrics.asset,
      assetMetrics.metrics,
    ]) ?? []
  );

  if (!reportAssetGroup.loaded) {
    return <reportAssetGroup.Fallback />;
  }
  if (!pricesChangesResult.loaded) {
    return <pricesChangesResult.Fallback />;
  }

  const columnDefs = [
    {
      headerName: 'Asset Details',
      colId: 'asset-details',
      marryChildren: true,
      children: [
        nameColumn(),
        symbolColumn({ initialHide: false }),
        ...reportAssetGroup.clusters.map((cluster) =>
          clusterColumn<AssetRowType>(cluster, reportAssetGroup.assetAndGroupClusterMapToGroup)
        ),
      ],
    },
    {
      headerName: 'Multifactors',
      colId: 'multifactors',
      marryChildren: true,
      children: scores?.length
        ? [
            ...scores.map((score) => {
              const scoresMap = new Map(score.assetScores.map((score) => [score.asset.id, score.score]));
              return {
                headerName: score.multifactor.multifactor.name,
                colId: `multifacor-${score.multifactor.multifactor.id}`,
                type: ['numericColumn'],
                valueGetter: (params: ValueGetterParams): number | undefined => {
                  if (!params.data) {
                    return undefined;
                  }

                  const score = scoresMap.get(params.data?.asset.id);
                  if (score) {
                    return Number.parseFloat(score);
                  }
                },
                valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value),
              };
            }),
          ]
        : [],
    },
    ...priceChangesGroupColumns<AssetRowType>(
      pricesChangesResult.priceSummary,
      pricesChangesResult.priceChanges24hByAssetId
    ),
    ...Object.entries(metricColumnsConfiguration).map(([groupHeader, groupColumns]) => ({
      headerName: groupHeader,
      marryChildren: true,
      children: groupColumns.map(
        ({ metricLabel, initialHide }): ColDef<{ asset: { id: string } }> =>
          assetMetricColumn(metricsByAsset, metricLabel, initialHide)
      ),
    })),
  ];

  const rowData = assets.filter((asset) => isAssetLabelInput(asset)).map((asset) => ({ asset }));

  return (
    <GAgGridPresets<AssetRowType>
      presetSettingsKey={presetKey}
      defaultPresets={assetScreenerDefaultPresets}
      rowData={rowData}
      sideBar={sidebar}
      enableCharts
      cellSelection
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      onGridReady={(val) => {
        if (isNil(defaultFilters)) {
          return;
        }

        // for some reason, when we don't set grid to null first, making an update to filter model causes aggrid to crash
        val.api?.setFilterModel(null);
        val.api?.setFilterModel(defaultFilters);
      }}
      onFilterChanged={(val) => {
        onFiltersChanged?.(val.api?.getFilterModel() ?? {});
      }}
    />
  );
};

export default AssetScreenerTable;
